<template>
  <Teleport to="body">
    <Transition name="modal">
      <div v-if="isOpen" class="wizard-backdrop" @click.self="handleClose">
        <div class="wizard-panel">

          <!-- Header -->
          <div class="wizard-header">
            <div>
              <p class="wizard-step-label">{{ stepLabel }}</p>
              <h2 class="wizard-title">{{ stepTitle }}</h2>
            </div>
            <button @click="handleClose" class="wizard-close">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <!-- Loading -->
          <div v-if="isLoading" class="wizard-loading">
            <div class="w-5 h-5 border-2 border-zinc-100 border-t-primary rounded-full animate-spin"></div>
            <p class="text-sm text-zinc-400">Loading your profile…</p>
          </div>

          <!-- Error -->
          <div v-else-if="error" class="wizard-error">
            <p class="text-sm text-zinc-500">{{ error }}</p>
            <button @click="fetchMasterData" class="wiz-btn-primary text-xs mt-3">Try Again</button>
          </div>

          <template v-else>

            <!-- Step 1: Choice -->
            <div v-if="wizardStep === 'choice'" class="wizard-body">
              <div class="grid grid-cols-2 gap-3">
                <button @click="selectPath('upload')" class="choice-card">
                  <svg class="w-5 h-5 text-zinc-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white mb-1">Upload CV</p>
                  <p class="text-xs text-zinc-400 leading-relaxed">Import an existing CV and we'll extract the data automatically</p>
                </button>
                <button @click="selectPath('profile')" class="choice-card">
                  <svg class="w-5 h-5 text-zinc-400 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                  <p class="text-sm font-semibold text-zinc-900 dark:text-white mb-1">From Profile</p>
                  <p class="text-xs text-zinc-400 leading-relaxed">Pick entries from your saved master profile</p>
                </button>
              </div>
            </div>

            <!-- Step 2: Metadata -->
            <div v-else-if="wizardStep === 'metadata'" class="wizard-body">
              <div class="grid grid-cols-2 gap-3">
                <div class="field-group col-span-2">
                  <label class="field-label">CV Title</label>
                  <input v-model="cvDetails.title" type="text" placeholder="e.g., McKinsey Application 2025" class="field-input" />
                </div>
                <div class="field-group">
                  <label class="field-label">Target Role</label>
                  <input v-model="cvDetails.target_role" type="text" placeholder="e.g., Associate" class="field-input" />
                </div>
                <div class="field-group">
                  <label class="field-label">Target Company <span class="field-optional">optional</span></label>
                  <input v-model="cvDetails.target_company" type="text" placeholder="e.g., McKinsey" class="field-input" />
                </div>
                <div class="field-group col-span-2">
                  <label class="field-label">Description <span class="field-optional">optional</span></label>
                  <input v-model="cvDetails.description" type="text" placeholder="Brief note about this CV" class="field-input" />
                </div>
                <div class="field-group col-span-2">
                  <label class="field-label">Job Description <span class="field-optional">optional</span></label>
                  <textarea
                    v-model="cvDetails.job_description"
                    rows="5"
                    placeholder="Paste the job posting here — the AI optimizer will tailor your bullet points to it"
                    class="field-input resize-y"
                  ></textarea>
                </div>
              </div>
            </div>

            <!-- Step 3: Upload -->
            <div v-else-if="wizardStep === 'upload'" class="wizard-body">
              <div
                @dragover.prevent
                @drop.prevent="e => { uploadedFile = e.dataTransfer.files[0] }"
                class="upload-zone"
                :class="{ 'upload-zone--filled': uploadedFile }"
              >
                <template v-if="!uploadedFile">
                  <svg class="w-8 h-8 text-zinc-300 mb-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1.5" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p class="text-sm text-zinc-500 mb-1">Drop your CV here or <label for="cv-upload" class="text-primary cursor-pointer hover:opacity-70">browse</label></p>
                  <p class="text-xs text-zinc-400">PDF, DOC, DOCX — max 5 MB · up to 5 pages</p>
                  <input type="file" id="cv-upload" @change="e => { uploadedFile = e.target.files[0] }" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt" class="hidden" />
                </template>
                <template v-else>
                  <svg class="w-6 h-6 text-primary mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p class="text-sm font-medium text-zinc-900 dark:text-white">{{ uploadedFile.name }}</p>
                  <p class="text-xs text-zinc-400 mt-0.5">{{ (uploadedFile.size / 1024 / 1024).toFixed(2) }} MB</p>
                  <button @click="uploadedFile = null" class="text-xs text-zinc-400 hover:text-zinc-600 mt-2">Choose different file</button>
                </template>
              </div>
              <p v-if="isUploading" class="text-xs text-zinc-400 text-center mt-3">{{ uploadProgress }} — this may take 10–15 seconds</p>
            </div>

            <!-- Step 4: Profile Selection -->
            <div v-else-if="wizardStep === 'selection'" class="wizard-body wizard-body--scroll">

              <!-- Education -->
              <div v-if="masterData.education?.length" class="selection-section">
                <p class="selection-label">Education</p>
                <div class="flex flex-col gap-3">
                  <div v-for="edu in masterData.education" :key="edu.id" class="flex gap-2 items-stretch">
                    <div
                      @click="toggleVersion(edu, 'education', edu.id)"
                      class="entry-card flex-1 min-w-0"
                      :class="{ 'entry-card--selected': isVersionSelected('education', edu.id) }"
                    >
                      <div v-if="isVersionSelected('education', edu.id)" class="entry-card-check">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate">{{ edu.program_name || edu.institution }}</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ edu.institution }}</p>
                      <p v-if="edu.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ edu.start_year }}–{{ edu.is_current ? 'Present' : edu.end_year }}</p>
                    </div>
                    <div
                      v-for="v in edu.variants"
                      :key="v.id"
                      @click="toggleVersion(edu, 'education', v.id)"
                      class="entry-card flex-1 min-w-0"
                      :class="{ 'entry-card--selected': isVersionSelected('education', v.id) }"
                    >
                      <div v-if="isVersionSelected('education', v.id)" class="entry-card-check">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <span class="entry-variant-pill">{{ v.variant_label }}</span>
                      <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate mt-1">{{ v.program_name || v.institution }}</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ v.institution }}</p>
                      <p v-if="v.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ v.start_year }}–{{ v.is_current ? 'Present' : v.end_year }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Experience -->
              <div v-if="masterData.experience?.length" class="selection-section">
                <p class="selection-label">Experience</p>
                <div class="flex flex-col gap-3">
                  <div v-for="exp in masterData.experience" :key="exp.id" class="flex gap-2 items-stretch">
                    <div
                      @click="toggleVersion(exp, 'experience', exp.id)"
                      class="entry-card flex-1 min-w-0"
                      :class="{ 'entry-card--selected': isVersionSelected('experience', exp.id) }"
                    >
                      <div v-if="isVersionSelected('experience', exp.id)" class="entry-card-check">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate">{{ exp.job_title }}</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ exp.company }}</p>
                      <p v-if="exp.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ exp.start_year }}–{{ exp.is_current ? 'Present' : exp.end_year }}</p>
                    </div>
                    <div
                      v-for="v in exp.variants"
                      :key="v.id"
                      @click="toggleVersion(exp, 'experience', v.id)"
                      class="entry-card flex-1 min-w-0"
                      :class="{ 'entry-card--selected': isVersionSelected('experience', v.id) }"
                    >
                      <div v-if="isVersionSelected('experience', v.id)" class="entry-card-check">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <span class="entry-variant-pill">{{ v.variant_label }}</span>
                      <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate mt-1">{{ v.job_title }}</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ v.company }}</p>
                      <p v-if="v.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ v.start_year }}–{{ v.is_current ? 'Present' : v.end_year }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Activities -->
              <div v-if="masterData.activities?.length" class="selection-section">
                <p class="selection-label">Activities & Leadership</p>
                <div class="flex flex-col gap-3">
                  <div v-for="act in masterData.activities" :key="act.id" class="flex gap-2 items-stretch">
                    <div
                      @click="toggleVersion(act, 'activity', act.id)"
                      class="entry-card flex-1 min-w-0"
                      :class="{ 'entry-card--selected': isVersionSelected('activity', act.id) }"
                    >
                      <div v-if="isVersionSelected('activity', act.id)" class="entry-card-check">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate">{{ act.role || act.organization }}</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ act.organization }}</p>
                      <p v-if="act.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ act.start_year }}–{{ act.is_current ? 'Present' : act.end_year }}</p>
                    </div>
                    <div
                      v-for="v in act.variants"
                      :key="v.id"
                      @click="toggleVersion(act, 'activity', v.id)"
                      class="entry-card flex-1 min-w-0"
                      :class="{ 'entry-card--selected': isVersionSelected('activity', v.id) }"
                    >
                      <div v-if="isVersionSelected('activity', v.id)" class="entry-card-check">
                        <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                      </div>
                      <span class="entry-variant-pill">{{ v.variant_label }}</span>
                      <p class="text-sm font-semibold text-zinc-900 dark:text-white leading-snug truncate mt-1">{{ v.role || v.organization }}</p>
                      <p class="text-xs text-zinc-500 dark:text-zinc-400 truncate mt-0.5">{{ v.organization }}</p>
                      <p v-if="v.start_year" class="text-[11px] text-primary/50 dark:text-primary-300/60 mt-1 font-medium">{{ v.start_year }}–{{ v.is_current ? 'Present' : v.end_year }}</p>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Skills -->
              <div v-if="masterData.skills?.length" class="selection-section">
                <p class="selection-label">Skills & Interests</p>
                <div class="flex flex-wrap gap-2">
                  <div
                    v-for="skill in masterData.skills"
                    :key="skill.id"
                    @click="toggleSkill(skill.id)"
                    class="entry-card"
                    style="flex: 0 1 calc(50% - 0.25rem)"
                    :class="{ 'entry-card--selected': isVersionSelected('skill_category', skill.id) }"
                  >
                    <div v-if="isVersionSelected('skill_category', skill.id)" class="entry-card-check">
                      <svg class="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="3" d="M5 13l4 4L19 7"/></svg>
                    </div>
                    <p class="text-sm font-semibold text-zinc-900 dark:text-white truncate">{{ skill.category_name }}</p>
                    <p class="text-xs text-zinc-500 dark:text-zinc-400 line-clamp-2 mt-0.5">{{ skill.content }}</p>
                  </div>
                </div>
              </div>

              <p v-if="!masterData.education?.length && !masterData.experience?.length && !masterData.activities?.length && !masterData.skills?.length"
                 class="text-sm text-zinc-400 text-center py-6">
                Your profile is empty. <router-link to="/profile" class="text-primary hover:opacity-70">Add entries first →</router-link>
              </p>
            </div>

          </template>

          <!-- Footer -->
          <div v-if="!isLoading && !error" class="wizard-footer">
            <button v-if="wizardStep !== 'choice'" @click="goBack" class="wiz-btn-secondary">
              ← Back
            </button>
            <span v-else></span>

            <button v-if="wizardStep === 'metadata'" @click="proceedToNextStep" :disabled="!hasValidForm" class="wiz-btn-primary">
              Continue
            </button>
            <button v-else-if="wizardStep === 'upload'" @click="uploadAndCreateCV" :disabled="!uploadedFile || isUploading" class="wiz-btn-primary">
              <span v-if="isUploading" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              {{ isUploading ? 'Processing…' : 'Create CV' }}
            </button>
            <button v-else-if="wizardStep === 'selection'" @click="createCV" :disabled="isCreating || !hasValidForm" class="wiz-btn-primary">
              <span v-if="isCreating" class="w-3.5 h-3.5 border-2 border-white border-t-transparent rounded-full animate-spin"></span>
              {{ isCreating ? 'Creating…' : 'Create CV' }}
            </button>
          </div>

        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRouter } from 'vue-router'
import { reportUpgradeRequired } from '../services/ApiService.js'
import { validateCvParserFile, parseCvParserError } from '../config/cvConfig.js'

export default {
  name: 'CvCreationWizard',
  props: {
    isOpen: { type: Boolean, required: true }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const router = useRouter()

    const isLoading  = ref(false)
    const isCreating = ref(false)
    const error      = ref(null)
    const wizardStep = ref('choice')
    const selectedPath = ref(null)

    const masterData      = ref({ education: [], experience: [], activities: [], skills: [] })
    const cvDetails       = ref({ title: '', description: '', target_role: '', target_company: '', job_description: '' })
    const selectedEntries = ref({ education_ids: [], experience_ids: [], activity_ids: [], skill_category_ids: [] })
    const uploadedFile    = ref(null)
    const isUploading     = ref(false)
    const uploadProgress  = ref('')

    const stepLabel = computed(() => {
      const labels = { choice: 'Step 1 of 3', metadata: 'Step 2 of 3', upload: 'Step 3 of 3', selection: 'Step 3 of 3' }
      return labels[wizardStep.value] || ''
    })
    const stepTitle = computed(() => {
      const titles = { choice: 'New CV', metadata: 'CV Details', upload: 'Upload CV', selection: 'Select Entries' }
      return titles[wizardStep.value] || ''
    })
    const hasValidForm = computed(() =>
      cvDetails.value.title.trim().length > 0 && cvDetails.value.target_role.trim().length > 0
    )

    const reset = () => {
      wizardStep.value = 'choice'
      selectedPath.value = null
      error.value = null
      cvDetails.value = { title: '', description: '', target_role: '', target_company: '', job_description: '' }
      selectedEntries.value = { education_ids: [], experience_ids: [], activity_ids: [], skill_category_ids: [] }
      uploadedFile.value = null
      isUploading.value = false
      uploadProgress.value = ''
    }

    const handleClose = () => { reset(); emit('close') }
    watch(() => props.isOpen, (open) => { if (open) reset() })

    const getCSRFToken = () => document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    const selectPath = (path) => { selectedPath.value = path; wizardStep.value = 'metadata' }

    const goBack = () => {
      if (wizardStep.value === 'metadata') { wizardStep.value = 'choice'; selectedPath.value = null }
      else if (wizardStep.value === 'upload' || wizardStep.value === 'selection') wizardStep.value = 'metadata'
    }

    const proceedToNextStep = () => {
      if (selectedPath.value === 'upload') wizardStep.value = 'upload'
      else if (selectedPath.value === 'profile') { wizardStep.value = 'selection'; fetchMasterData() }
    }

    const fetchMasterData = async () => {
      try {
        isLoading.value = true
        error.value = null
        const res  = await fetch('/api/master_data/all', { headers: { Accept: 'application/json', 'X-CSRF-Token': getCSRFToken() } })
        const data = await res.json()
        if (data.success) {
          masterData.value = data.data
          selectedEntries.value.education_ids      = data.data.education.map(e => e.id)
          selectedEntries.value.experience_ids     = data.data.experience.map(e => e.id)
          selectedEntries.value.activity_ids       = data.data.activities.map(e => e.id)
          selectedEntries.value.skill_category_ids = data.data.skills.map(e => e.id)
        } else {
          error.value = data.message || 'Failed to load profile data'
        }
      } catch { error.value = 'Failed to connect to server.' }
      finally { isLoading.value = false }
    }

    const familyIds = (entry) => [entry.id, ...(entry.variants || []).map(v => v.id)]

    // Check if a specific version ID is currently selected
    const isVersionSelected = (type, id) => {
      const key = type === 'skill_category' ? 'skill_category_ids' : `${type}_ids`
      return selectedEntries.value[key].includes(id)
    }

    // Click a card: if already selected → deselect the whole family; if not → select this version, deselect others in family
    const toggleVersion = (entry, type, versionId) => {
      const key = `${type}_ids`
      const ids = selectedEntries.value[key]
      const fids = familyIds(entry)
      if (ids.includes(versionId)) {
        selectedEntries.value[key] = ids.filter(id => !fids.includes(id))
      } else {
        selectedEntries.value[key] = [...ids.filter(id => !fids.includes(id)), versionId]
      }
    }

    const toggleSkill = (id) => {
      const ids = selectedEntries.value.skill_category_ids
      selectedEntries.value.skill_category_ids = ids.includes(id)
        ? ids.filter(i => i !== id)
        : [...ids, id]
    }

    const uploadAndCreateCV = async () => {
      if (!uploadedFile.value || !hasValidForm.value) return

      const fileError = validateCvParserFile(uploadedFile.value)
      if (fileError) {
        error.value = fileError
        return
      }

      try {
        isUploading.value = true
        uploadProgress.value = 'Uploading file…'
        const formData = new FormData()
        formData.append('file', uploadedFile.value)
        formData.append('cv[title]', cvDetails.value.title)
        formData.append('cv[description]', cvDetails.value.description)
        formData.append('cv[target_role]', cvDetails.value.target_role)
        formData.append('cv[target_company]', cvDetails.value.target_company)
        formData.append('cv[job_description]', cvDetails.value.job_description)
        uploadProgress.value = 'Processing your CV…'
        const res  = await fetch('/api/cvs/create_from_upload', {
          method: 'POST', headers: { Accept: 'application/json', 'X-CSRF-Token': getCSRFToken() }, body: formData
        })
        const data = await res.json()
        if (data.success) { handleClose(); router.push(`/cv-creator/${data.cv.id}`) }
        else if (res.status === 402) { handleClose(); reportUpgradeRequired(data) }
        else error.value = parseCvParserError(res, data)
      } catch { error.value = 'Failed to upload CV. Please try again.' }
      finally { isUploading.value = false; uploadProgress.value = '' }
    }

    const createCV = async () => {
      if (!hasValidForm.value) return
      try {
        isCreating.value = true
        const res  = await fetch('/api/cvs/create_from_master', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-Token': getCSRFToken() },
          body: JSON.stringify({ cv: cvDetails.value, selected_entries: selectedEntries.value })
        })
        const data = await res.json()
        if (data.success) { handleClose(); router.push(`/cv-creator/${data.cv.id}`) }
        else if (res.status === 402) { handleClose(); reportUpgradeRequired(data) }
        else error.value = data.message || 'Failed to create CV'
      } catch { error.value = 'Failed to create CV. Please try again.' }
      finally { isCreating.value = false }
    }

    return {
      isLoading, isCreating, error,
      wizardStep, selectedPath, masterData, cvDetails, selectedEntries,
      hasValidForm, uploadedFile, isUploading, uploadProgress,
      stepLabel, stepTitle,
      handleClose, selectPath, goBack, proceedToNextStep,
      fetchMasterData, uploadAndCreateCV, createCV,
      isVersionSelected, toggleVersion, toggleSkill
    }
  }
}
</script>

<style scoped>
.wizard-backdrop {
  @apply fixed inset-0 z-50 flex items-center justify-center p-4;
  background: rgba(0, 0, 0, 0.25);
  backdrop-filter: blur(4px);
}
.wizard-panel {
  @apply bg-white dark:bg-gray-950 border border-zinc-100 dark:border-zinc-800 rounded-2xl w-full max-w-2xl flex flex-col overflow-hidden;
  max-height: 90vh;
}
.wizard-header {
  @apply flex items-start justify-between px-6 pt-6 pb-5 border-b border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.wizard-step-label {
  @apply text-xs font-semibold uppercase tracking-widest text-gold dark:text-gold-400 mb-1;
}
.wizard-title {
  @apply text-lg font-semibold text-zinc-900 dark:text-white;
}
.wizard-close {
  @apply p-1.5 rounded-lg text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors;
}
.wizard-loading {
  @apply flex flex-col items-center justify-center gap-3 py-12;
}
.wizard-error {
  @apply flex flex-col items-center justify-center py-10;
}
.wizard-body {
  @apply px-6 py-5 flex-1;
}
.wizard-body--scroll {
  @apply overflow-y-auto;
}
.wizard-footer {
  @apply flex items-center justify-between px-6 py-4 border-t border-zinc-100 dark:border-zinc-800 flex-shrink-0;
}
.wiz-btn-primary {
  @apply inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium text-white bg-primary hover:opacity-90 disabled:opacity-40 disabled:cursor-not-allowed transition-opacity;
}
.wiz-btn-secondary {
  @apply inline-flex items-center px-3 py-2 rounded-lg text-sm font-medium text-zinc-500 hover:text-zinc-900 dark:hover:text-white transition-colors;
}
.choice-card {
  @apply flex flex-col items-start text-left p-5 border border-zinc-100 dark:border-zinc-800 rounded-xl hover:border-primary dark:hover:border-primary-400 transition-colors cursor-pointer;
}
.field-group {
  @apply flex flex-col gap-1;
}
.field-label {
  @apply text-xs font-medium text-zinc-500 dark:text-zinc-400;
}
.field-optional {
  @apply font-normal text-zinc-400;
}
.field-input {
  @apply w-full px-3 py-2 text-sm border border-zinc-200 dark:border-zinc-700 rounded-lg bg-white dark:bg-zinc-900 text-zinc-900 dark:text-white placeholder-zinc-300 dark:placeholder-zinc-600 focus:outline-none focus:border-primary dark:focus:border-primary-400 transition-colors;
}
.upload-zone {
  @apply border border-dashed border-zinc-200 dark:border-zinc-700 rounded-xl p-10 flex flex-col items-center text-center hover:border-primary dark:hover:border-primary-400 transition-colors;
}
.upload-zone--filled {
  @apply border-solid border-primary dark:border-primary-400;
}
.selection-section {
  @apply mb-5;
}
.selection-label {
  @apply text-xs font-semibold uppercase tracking-widest text-zinc-400 dark:text-zinc-500 mb-2;
}
.entry-card {
  @apply relative border border-zinc-100 dark:border-zinc-800 rounded-xl p-3 cursor-pointer transition-colors hover:border-zinc-300 dark:hover:border-zinc-600;
}
.entry-card--selected {
  @apply border-gold dark:border-gold-500 bg-gold-50/40 dark:bg-gold-900/10;
}
.entry-card-check {
  @apply absolute top-2 right-2 w-4 h-4 rounded-full bg-gold flex items-center justify-center flex-shrink-0;
}
.entry-variant-pill {
  @apply inline-flex items-center border border-zinc-200 dark:border-zinc-700 rounded-full text-[10px] font-medium text-zinc-500 dark:text-zinc-400 px-2 py-0.5 leading-tight;
}
.modal-enter-active, .modal-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.97) translateY(4px);
}
</style>
